import { useEffect, useRef, useState } from 'react';

interface ProgressRailProps {
  rank: string;
  nextRank: string | null;
  /** Completion points still needed for `nextRank`. */
  toNext: number;
  xp: number;
  maxXp: number;
  percent: number;
  /** One tick per step, so the rail reads as a run of film rather than a bar. */
  steps: number;
  stepsDone: number;
}

/**
 * The persistent header of the induction: what you are, what you would be next,
 * and how far that is. Everything on it is derived from the answers, so there is
 * nothing here the person cannot work out for themselves.
 */
export default function ProgressRail({
  rank,
  nextRank,
  toNext,
  xp,
  maxXp,
  percent,
  steps,
  stepsDone
}: ProgressRailProps) {
  const shown = useCountUp(xp);

  return (
    <div className="ob-top">
      <div className="ob-line">
        <span className="ob-rank">
          <span className="mono">Rank</span>
          <span className="lbl">{rank}</span>
        </span>
        <span className="ob-xp">
          <b>{shown.toLocaleString()}</b> / {maxXp.toLocaleString()} XP
        </span>
      </div>

      <div className="rail" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} aria-label="Profile completion">
        <i style={{ width: `${percent}%` }} />
        {Array.from({ length: Math.max(0, steps - 1) }, (_, i) => (
          <s key={i} className={i < stepsDone - 1 ? 'done' : ''} style={{ left: `${((i + 1) / steps) * 100}%` }} />
        ))}
      </div>

      <p className="mono" style={{ marginTop: 10 }}>
        {nextRank ? `${toNext}% to ${nextRank}` : 'Top of the ladder'} · {percent}% complete
      </p>
    </div>
  );
}

/**
 * Rolls the XP number rather than snapping it. The jump is the reward, and a
 * number that changes between two frames is a number nobody sees change.
 *
 * `shown` tracks what is actually on screen, not where the last run began, so a
 * second award mid-roll continues from the visible number instead of replaying
 * ground already covered. A hidden tab gets the value outright — requestAnimation
 * Frame does not run in the background, and a frozen counter is worse than an
 * unanimated one.
 */
function useCountUp(target: number, ms = 650): number {
  const [value, setValue] = useState(target);
  const shown = useRef(target);

  useEffect(() => {
    const start = shown.current;
    if (start === target) return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || document.hidden) {
      shown.current = target;
      setValue(target);
      return;
    }

    let frame = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / ms);
      const next = Math.round(start + (target - start) * (1 - (1 - p) ** 3));
      shown.current = next;
      setValue(next);
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, ms]);

  return value;
}
